/**
 * Shared URL search-param keys for the booking select step.
 *
 * Written by MupiSearchBar when navigating into booking; read by useBookingParams.
 * Legacy keys (`step`, `centerLat`, `centerLng`) are still emitted by the search
 * bar for v1 compatibility but are intentionally ignored by the v2 reader —
 * v2 uses `lat`/`lng`/`radiusKm` for viewport search instead.
 */
export const BOOKING_URL_PARAMS = {
  location: "location",
  startDate: "startDate",
  endDate: "endDate",
  lat: "lat",
  lng: "lng",
  zoom: "zoom",
  radiusKm: "radiusKm",
  sort: "sort",
  mupi: "mupi",
  insight: "insight",
  formatId: "formatId",
  // Screens the map opens on, once (MPE-1411 Book again). Removed after the map has used it.
  focusScreens: "focusScreens",
  // The same Screens, kept once `focusScreens` is removed (MPE-1411 Book again): the map keeps
  // drawing those the search offers, wherever it pages them, and fits them after a reload that came
  // before the map could. Durable; a search for a destination replaces the URL without it.
  rebookedScreens: "rebookedScreens",
  // Opens the location/date search once, for new dates (MPE-1411 Book again, original dates
  // expired). Removed as soon as the page has read it.
  chooseDates: "chooseDates",
  // The dates on the page are not the buyer's yet (MPE-1411 Book again, original dates expired).
  // Durable: survives closing the search, a reload and navigation. It goes when a search with a
  // valid range replaces the URL, or when the cart holds a selection, whose dates are then the page's.
  datesUnconfirmed: "datesUnconfirmed",
  // Legacy major-unit price bounds. Ignored by the v2 reader; cleared on write:
  priceMin: "priceMin",
  priceMax: "priceMax",
  // Unused by v2 reader — environment was dropped from booking filters:
  environment: "environment",
  // Legacy / unused by v2 reader — kept here so the contract is visible:
  step: "step",
  centerLat: "centerLat",
  centerLng: "centerLng",
} as const

export type BookingUrlParam = (typeof BOOKING_URL_PARAMS)[keyof typeof BOOKING_URL_PARAMS]
